//
// Copyright (c) 2025 Comcast
//

import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import startCase from 'lodash/startCase'
import styled from 'styled-components'

import * as sessionUserSelector from 'selectors/sessionUser'
import { getFieldValue } from 'utils/ticketFields'
import Sidebar from 'components/Shared/Sidebar/RightSidebar'
import SidebarSection from 'components/Shared/Sidebar/SidebarSection/Index'
import SidebarText from 'components/Shared/Sidebar/SidebarText/Index'
import SidebarSectionSecureText from 'components/Shared/Sidebar/SidebarSectionSecureText/Index'
import SidebarTicketExternalIdSection from 'components/Shared/Sidebar/SidebarTicketExternalIdSection/Index'
import SidebarTicketUserSection from 'components/Shared/Sidebar/SidebarTicketUserSection/Index'
import SidebarSectionTable from 'components/Shared/Sidebar/SidebarSectionTable/Index'
import SidebarTicketParent from 'components/Shared/Sidebar/SidebarTicketParent/Index'
import TicketTypeBadge from 'components/App/TicketsPage/shared/TicketTypeBadge'
import SidebarBadgesSection from 'components/App/TicketsPage/shared/SidebarBadgesSection'

import { RouteTicketProviderTicket } from 'routes'

const TypeFieldValue = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`

const ChildrenSection = ({ ticket }) => {
  const { t } = useTranslation()

  if (!ticket.childTicketsCount || !ticket.hierarchyEnabled) {
    return null
  }

  const values = ticket.childTickets.map(childTicket => ({
    id: childTicket.number,
    title: childTicket.title || childTicket.number,
    link: RouteTicketProviderTicket({
      ticketProviderId: ticket.ticketProviderId,
      ticketId: childTicket.number,
    }),
  }))

  return (
    <SidebarSectionTable
      title={t('ticketProviderTicketPage.sidebarSectionChildren.title')}
      values={values}
      columnsOrder={['id', 'title']}
      columns={{
        'id': {
          title: t('ticketProviderTicketPage.sidebarSectionChildren.columns.id'),
          noWrap: true,
        },
        'title': {
          title: t('ticketProviderTicketPage.sidebarSectionChildren.columns.title'),
          format: 'link',
          linkKey: 'link',
        },
      }}
    />
  )
}

ChildrenSection.propTypes = {
  ticket: PropTypes.shape({
    childTicketsCount: PropTypes.number,
    childTickets: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      number: PropTypes.string,
      title: PropTypes.string,
    })),
    ticketProviderId: PropTypes.string,
    hierarchyEnabled: PropTypes.bool,
  }).isRequired,
}

const TicketSidebar = ({ ticket, onClose, searchValue }) => {
  const { t } = useTranslation()
  const userTimeZone = useSelector(sessionUserSelector.timezone)

  if (!ticket) {
    return null
  }

  return (
    <Sidebar
      onClose={onClose}
      title={t('ticketProviderTicketPage.sidebarDetails')}
      dataTest='tickets-page-details'
    >
      <SidebarTicketExternalIdSection
        externalId={ticket.externalId}
        externalTicketLink={ticket.externalTicketLink}
      />

      <SidebarSection title={t('ticketProviderTicketPage.sidebarSectionRelayId')} copyText={ticket.id}>
        <SidebarText text={ticket.id} markedPart={searchValue} />
      </SidebarSection>

      {ticket.hierarchyEnabled && (
        <SidebarSection title={t('ticketProviderTicketPage.sidebarSectionType')} copyText={ticket.type}>
          <TypeFieldValue data-test='ticket-type-value'>
            <TicketTypeBadge ticketType={ticket.type} noMargin />
            <SidebarText text={ticket.type} markedPart={searchValue} />
          </TypeFieldValue>
        </SidebarSection>
      )}

      {ticket.hierarchyEnabled && (
        <SidebarTicketParent
          parentTicket={ticket.parentTicket}
          ticketProviderId={ticket.ticketProviderId}
        />
      )}

      <ChildrenSection ticket={ticket} />

      <SidebarTicketUserSection
        userName={ticket.createByUser}
        userAvatarUrl={ticket.userAvatarUrl}
      />

      <div>
        {ticket.previewFields.map(field => {
          if (field.fieldType === 'array') {
            return (
              <SidebarBadgesSection field={field} key={field.id} />
            )
          }

          const value = getFieldValue(field, userTimeZone)
          const isSecure = field.fieldType === 'encrypted_text'
          return (
            <SidebarSectionSecureText
              title={startCase(field.name)}
              key={field.id}
              tooltipText={field.tooltiptext}
              value={value}
              isSecure={isSecure}
              markedPart={searchValue}
            />
          )
        })}
      </div>
    </Sidebar>
  )
}

TicketSidebar.propTypes = {
  ticket: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  searchValue: PropTypes.string,
}

export default TicketSidebar
